import { US_States } from 'lib/constants';
import {
  KEBAB_AND_CAKE_COMPANY_ID,
  OCEANS_COMPANY_ID,
  REFINED_SIGNS_COMPANY_ID,
  TOURY_AI_COMPANY_ID,
} from 'lib/whitelistConstants';
import { ICompany } from 'types';

/**
 * Gets the appropriate label for the externalId field based on the company
 * @param companyId - The company ID
 * @returns The label to use for the externalId field
 */
export const getExternalIdLabel = (companyId?: string): string => {
  if (companyId === OCEANS_COMPANY_ID) {
    return 'Diver Number';
  }
  return 'External ID';
};

/**
 * Gets the appropriate placeholder for the externalId field based on the company
 * @param companyId - The company ID
 * @returns The placeholder to use for the externalId field
 */
export const getExternalIdPlaceholder = (companyId?: string): string => {
  if (companyId === OCEANS_COMPANY_ID) {
    return 'Diver Number';
  }
  return 'External User ID';
};

/**
 * Gets the appropriate tooltip content for the externalId field based on the company
 * @param companyId - The company ID
 * @returns The tooltip content to use for the externalId field
 */
export const getExternalIdTooltip = (companyId?: string): string => {
  if (companyId === OCEANS_COMPANY_ID) {
    return `Your worker's diver number.`;
  }
  return 'The ID of this contract or contractor in one of your external systems. This does not need to be unique between different contracts.';
};

/**
 * Exposes the add employee flow for non-live W2 companies
 * @param companyId - The company ID
 * @returns True if the add employee flow is exposed, false otherwise
 */
export const exposeAddEmployeeFlowForNonLiveW2Company = (companyId?: string): boolean => {
  return (
    companyId === TOURY_AI_COMPANY_ID ||
    companyId === REFINED_SIGNS_COMPANY_ID ||
    companyId === KEBAB_AND_CAKE_COMPANY_ID
  );
};

/**
 * Why a Check company is restricted from running payroll.
 * - `terminated`: the company has been closed in Check (`active === false`).
 * - `blocked`: Check is holding the company out of good standing (`inGoodStanding === false`),
 *   e.g. after a failed payroll or a compliance/funding hold. They can't run additional payroll
 *   until it's resolved.
 */
export type CheckCompanyRestrictionReason = 'terminated' | 'blocked';

/**
 * Returns why a Check company is restricted (terminated or blocked), or null if it's in good
 * standing. Missing/unloaded data fails open (returns null) so a slow or unavailable Check fetch
 * never hides action items or locks payroll for an otherwise-healthy company.
 * @param checkCompany - The check company object from useCheckCompanySWR
 */
export const getCheckCompanyRestriction = (checkCompany?: {
  active?: boolean;
  inGoodStanding?: boolean;
}): CheckCompanyRestrictionReason | null => {
  if (!checkCompany) return null;
  if (checkCompany.active === false) return 'terminated';
  if (checkCompany.inGoodStanding === false) return 'blocked';
  return null;
};

/**
 * Convenience boolean for {@link getCheckCompanyRestriction}: true when the company is terminated
 * or blocked by Check.
 * @param checkCompany - The check company object from useCheckCompanySWR
 */
export const isCheckCompanyRestricted = (checkCompany?: { active?: boolean; inGoodStanding?: boolean }): boolean =>
  getCheckCompanyRestriction(checkCompany) !== null;

/**
 * Whether we should still be prompting this company about workers' comp. Mirrors the backend's
 * CompanyUtils.needsWorkersComp: buying coverage and declaring the company exempt both stop the
 * prompts, and they mean different things, so neither one alone is the question to ask.
 */
export const needsWorkersComp = (flags?: ICompany['flags']): boolean =>
  !flags?.hasWorkersCompCoverage && !flags?.workersCompExempt;

/**
 * Checks if the company's Check implementation (registration, address verification, KYB) is
 * still pending. Hiring in a new state later re-opens Check's onboard status to "blocking"
 * (new setup parameters / filing authorizations) but leaves implementation "completed", so
 * live companies with pending jurisdiction paperwork deliberately pass this check — that
 * paperwork is surfaced via the in-flow tax setup step and the payroll dashboard banner
 * instead of locking them out of hiring and payroll.
 * @param checkCompany - The check company object from useCheckCompanySWR
 * @returns True if implementation is not complete
 */
export const isCheckImplementationIncomplete = (checkCompany?: { implementation?: { status?: string } }): boolean => {
  // Missing data fails open: blocking on an unloaded or unavailable Check company would
  // lock hiring and payroll for reasons unrelated to the company's actual status.
  if (!checkCompany) {
    return false;
  }
  return checkCompany.implementation?.status !== 'completed';
};

/**
 * Checks if the company owes jurisdiction tax setup in Check (e.g. a state registration that
 * appeared after hiring an employee in a new state).
 * @param checkCompany - The check company object from useCheckCompanySWR
 * @returns True if any setup parameter is blocking
 */
export const hasBlockingSetupParameters = (checkCompany?: {
  onboard?: { setupParameters?: { status?: string }[] };
}): boolean => {
  return Boolean(checkCompany?.onboard?.setupParameters?.some((param) => param.status === 'blocking'));
};

/**
 * Checks if the company owes filing authorization in Check. Like a blocking Federal setup
 * parameter this is company-wide rather than per-employee, and Check treats it as
 * payroll-blocking (mirrored on the Taxes page), so the payroll run screens must gate Submit
 * on it in addition to the per-state setup parameters.
 * @param checkCompany - The check company object from useCheckCompanySWR
 * @returns True if any filing authorization is blocking
 */
export const hasBlockingFilingAuthorization = (checkCompany?: {
  onboard?: { filingAuthorization?: { status?: string }[] };
}): boolean => {
  return Boolean(checkCompany?.onboard?.filingAuthorization?.some((item) => item.status === 'blocking'));
};

/**
 * Checks if any filing authorization form is actually signable now. A form is signable when it
 * has needs_attention status AND its jurisdiction's setup parameter is not needs_attention or blocking.
 * @param checkCompany - The check company object from useCheckCompanySWR
 * @returns True if any filing authorization item is ready to sign
 */
export const hasSignableFilingAuthorization = (checkCompany?: {
  onboard?: {
    filingAuthorization?: { name?: string; status?: string }[];
    setupParameters?: { name?: string; status?: string }[];
  };
}): boolean => {
  const pendingJurisdictionNames = (checkCompany?.onboard?.setupParameters ?? [])
    .filter((param) => param.status === 'needs_attention' || param.status === 'blocking')
    .map((param) => param.name)
    .filter((name): name is string => Boolean(name));

  return Boolean(
    checkCompany?.onboard?.filingAuthorization?.some((item) => {
      if (item.status !== 'needs_attention' || !item.name) return false;
      return !pendingJurisdictionNames.some((jurisdictionName) => item.name?.startsWith(jurisdictionName));
    }),
  );
};

export interface IRunEmployeeBlockingJurisdictions {
  employeeId: string;
  employeeName: string;
  jurisdictions: string[];
}

/**
 * Maps the employees in a payroll run to their workplace states and returns the ones whose
 * state (or Federal) tax setup is still blocking in Check. Jurisdiction names from Check are
 * full state names ("Colorado") while workplace addresses carry two-letter codes ("CO"), so
 * the comparison goes through the US_States map. A run containing only employees in fully
 * registered states returns an empty list and payroll can proceed.
 * @param checkCompany - The check company object from useCheckCompanySWR
 * @param runEmployeeIds - Check employee ids contained in the payroll run
 * @param checkEmployees - Check employees (id, name, workplace ids)
 * @param workplaces - Check workplaces (id, address.state)
 * @returns The affected employees with the jurisdictions blocking them
 */
export const getRunEmployeesInBlockingJurisdictions = ({
  checkCompany,
  runEmployeeIds,
  checkEmployees,
  workplaces,
}: {
  checkCompany?: { onboard?: { setupParameters?: { name?: string; status?: string }[] } };
  runEmployeeIds: string[];
  checkEmployees: { id?: string; firstName?: string; lastName?: string; workplaces?: string[] }[];
  workplaces: { id?: string; address?: { state?: string } }[];
}): IRunEmployeeBlockingJurisdictions[] => {
  const blockingJurisdictionNames = new Set(
    (checkCompany?.onboard?.setupParameters || [])
      .filter((param) => param.status === 'blocking')
      .map((param) => param.name)
      .filter(Boolean),
  );
  if (blockingJurisdictionNames.size === 0 || runEmployeeIds.length === 0) {
    return [];
  }

  const isFederalBlocking = blockingJurisdictionNames.has('Federal');
  const stateNameByCode = US_States as Record<string, string>;
  const workplaceStateById = new Map(workplaces.map((workplace) => [workplace.id, workplace.address?.state]));
  const employeeById = new Map(checkEmployees.map((employee) => [employee.id, employee]));

  return runEmployeeIds.flatMap((employeeId) => {
    const employee = employeeById.get(employeeId);
    if (!employee) {
      return [];
    }
    const blockedStateNames = (employee.workplaces || [])
      .map((workplaceId) => workplaceStateById.get(workplaceId))
      .filter((stateCode): stateCode is string => Boolean(stateCode))
      // Check has returned full state names ("Colorado") for jurisdictions so far; also
      // match the raw two-letter code in case a jurisdiction ever arrives abbreviated.
      .filter(
        (stateCode) =>
          blockingJurisdictionNames.has(stateNameByCode[stateCode]) || blockingJurisdictionNames.has(stateCode),
      )
      .map((stateCode) => stateNameByCode[stateCode] || stateCode);
    const jurisdictions = [...new Set(isFederalBlocking ? ['Federal', ...blockedStateNames] : blockedStateNames)];
    if (jurisdictions.length === 0) {
      return [];
    }
    const employeeName = [employee.firstName, employee.lastName].filter(Boolean).join(' ') || employeeId;
    return [{ employeeId, employeeName, jurisdictions }];
  });
};

/**
 * Checks if payroll FBO onboarding is approved
 * @param company - The company object
 * @returns True if payroll FBO onboarding is approved, false otherwise
 */
export const isPayrollFBOOnboardingApproved = (company?: {
  tp?: {
    payrollFboOnboardingStage?:
      'BANKING_DETAILS' | 'SUBMITTED' | 'DISBURSEMENT_ACCOUNT_APPROVED' | 'DISBURSEMENT_ACCOUNT_REJECTED';
  };
  theraBanking?: boolean;
}): boolean => {
  // If theraBanking is false, don't block regardless of TP approval status
  if (company?.theraBanking !== true) {
    return true;
  }

  // If theraBanking is true, check TP approval status
  return company?.tp?.payrollFboOnboardingStage === 'DISBURSEMENT_ACCOUNT_APPROVED';
};

/**
 * Checks if payroll FBO onboarding is pending (not approved)
 * @param company - The company object
 * @returns True if payroll FBO onboarding is pending, false otherwise
 */
export const isPayrollFBOOnboardingPending = (company?: {
  tp?: {
    payrollFboOnboardingStage?:
      'BANKING_DETAILS' | 'SUBMITTED' | 'DISBURSEMENT_ACCOUNT_APPROVED' | 'DISBURSEMENT_ACCOUNT_REJECTED';
  };
  theraBanking?: boolean;
  address?: {
    country?: string;
  };
}): boolean => {
  // If theraBanking is false, don't block regardless of TP approval status
  if (company?.theraBanking !== true || company?.address?.country !== 'US') {
    return false;
  }

  // If theraBanking is true, check if TP is pending
  const stage = company?.tp?.payrollFboOnboardingStage;
  return stage === 'BANKING_DETAILS' || stage === 'SUBMITTED' || stage === 'DISBURSEMENT_ACCOUNT_REJECTED';
};
